import React, { useState, useEffect, useMemo } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  IconButton,
} from '@mui/material' 
import {
  DirectionsBike as VehicleIcon,
  Assessment as AnalyticsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Engineering as MaintenanceIcon,
  Assignment as AssignmentIcon,
  Factory as OEMIcon,
} from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import { useQuery } from 'react-query'
import { hubService, HubResponse } from '../services/hubService'
import { vehicleService, VehicleStatusBackupLedger } from '../services/vehicleService'
import ordersService, { FullDataDailyRow } from '../services/ordersService'
import { buildOemModelStatusMatrix } from '../utils/oemModelStatusMatrix'

const UPTIME_OPERATIONAL_STATUSES = [
  'Assigned',
  'Assigned - Team Use',
  'Available',
  'Available - Without(Battery & Charger)',
];

const DOWNTIME_OPERATIONAL_STATUSES = [
  'Returned',
  'Under Maintenance',
  'Under Preventive Maintenance',
  'Under Maintenance - Spare Issue',
  'Under Maintenance - Major Repair',
  'Under Recovery',
  'Under Police Custody / FIR',
  'Under Maintenance - External Work',
  'Under Maintenance - IoT Failure',
  'Under Maintenance - Accidental / Insurance Claim',
  'Under Warranty Claim',
  'Under PDI',
];

const SCRAP_OPERATIONAL_STATUSES = [
  'Asset Missing',
  'To be Scrapped / Returned to Lessor',
];

const OPERATIONAL_STATUS_DISPLAY_ORDER = [
  ...UPTIME_OPERATIONAL_STATUSES,
  ...DOWNTIME_OPERATIONAL_STATUSES,
  ...SCRAP_OPERATIONAL_STATUSES,
];

const OPERATIONAL_STATUS_GROUP_COLORS = {
  uptime: '#22c55e',
  downtime: '#ff4dff',
  scrap: '#ff3b5c',
};

const normalizeStatusLookupKey = (status: string) =>
  String(status || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '');

const OPERATIONAL_STATUS_ALIASES: Record<string, string> = {
  [normalizeStatusLookupKey('Available - Without(Battery & Charger)')]: 'Available - Without(Battery & Charger)',
  [normalizeStatusLookupKey('Available - Without (Battery & Charger)')]: 'Available - Without(Battery & Charger)',
  [normalizeStatusLookupKey('Under Maintenance - Extenal Work')]: 'Under Maintenance - External Work',
  [normalizeStatusLookupKey('Under Maintenance - External Work')]: 'Under Maintenance - External Work',
  [normalizeStatusLookupKey('Under maintenance - Major Repair')]: 'Under Maintenance - Major Repair',
  [normalizeStatusLookupKey('Under Maintenance - Major Repair')]: 'Under Maintenance - Major Repair',
  [normalizeStatusLookupKey('Asset Missing')]: 'Asset Missing',
  [normalizeStatusLookupKey('Missing')]: 'Asset Missing',
  [normalizeStatusLookupKey('To be Scrapped / Returned to Lessor')]: 'To be Scrapped / Returned to Lessor',
  [normalizeStatusLookupKey('To Be Scrapped / Returned to Lessor')]: 'To be Scrapped / Returned to Lessor',
  [normalizeStatusLookupKey('Scrapped /  / Returned to Lessor')]: 'To be Scrapped / Returned to Lessor',
  [normalizeStatusLookupKey('Scrapped / Returned to Lessor')]: 'To be Scrapped / Returned to Lessor',
  [normalizeStatusLookupKey('To be Scrapped / Testing Vehicle')]: 'To be Scrapped / Testing Vehicle',
  [normalizeStatusLookupKey('To Be Scrapped / Testing Vehicle')]: 'To be Scrapped / Testing Vehicle',
};

const normalizeOperationalStatus = (status: string) => {
  const trimmedStatus = String(status || '').trim();
  if (!trimmedStatus) return '-';
  return OPERATIONAL_STATUS_ALIASES[normalizeStatusLookupKey(trimmedStatus)] || trimmedStatus;
};

const toInputDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getMonthKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const getMonthRange = (monthKey: string, today: Date) => {
  const [year, month] = monthKey.split('-').map(Number);
  const start = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const yesterday = addDays(today, -1);
  const end = monthKey === getMonthKey(today) && yesterday < monthEnd ? yesterday : monthEnd;

  return {
    dateFrom: toInputDate(start),
    dateTo: toInputDate(end < start ? start : end),
  };
};

const getRecentMonthOptions = (today: Date, count = 6) => Array.from({ length: count }, (_, index) => {
  const date = new Date(today.getFullYear(), today.getMonth() - index, 1);
  const value = getMonthKey(date);
  const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return { value, label };
});

const formatLogHeaderDate = (date: string) => {
  const value = new Date(`${date}T00:00:00`);
  return value.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-');
};

const formatFullDataHeaderDate = (date: string) => {
  const value = new Date(`${date}T00:00:00`);
  return value.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, '-');
};

const formatFullDataRangeLabel = (from: string, to: string) =>
  `${formatFullDataHeaderDate(from)} -> ${formatFullDataHeaderDate(to)}`;

const getDailyParameterValue = (
  ledger: VehicleStatusBackupLedger,
  date: string,
  parameterNames: string[],
) => {
  const report = ledger.dailyReport?.find((item) => item.date === date);
  if (!report) return 0;

  const targets = parameterNames.map((name) => name.toLowerCase());
  return report.rows.reduce((sum, row) => (
    targets.includes(row.parameter.toLowerCase()) ? sum + row.value : sum
  ), 0);
};

type DeploymentMetricKey = 'deployed' | 'return' | 'net';

type HubDeploymentRow = {
  hubId: string;
  hubName: string;
  values: Record<string, {
    deployed: number;
    return: number;
    net: number;
  }>;
  totals: {
    deployed: number;
    return: number;
    net: number;
  };
};

const DEPLOYMENT_METRICS: Array<{
  key: DeploymentMetricKey;
  label: string;
}> = [
  { key: 'deployed', label: '1. Deployed' },
  { key: 'return', label: '2. Return' },
  { key: 'net', label: '3. Net Deployment' },
];

const DEPLOYED_STATUS_NAMES = ['Assigned', 'Assigned - Team Use'];
const READY_FLEET_STATUS_NAMES = [
  'Available',
  'Available - Without(Battery & Charger)',
];

const LEGAL_SCRAP_STATUS_NAMES = [
  ...SCRAP_OPERATIONAL_STATUSES,
  'To be Scrapped / Testing Vehicle',
  'To Be Scrapped / Testing Vehicle',
];

const getStatusBucketCount = (
  statusCounts: Record<string, number>,
  bucketStatuses: string[],
) => {
  const normalizedBucket = new Set(bucketStatuses.map((status) => normalizeOperationalStatus(status).toLowerCase()));

  return Object.entries(statusCounts).reduce((sum, [status, count]) => (
    normalizedBucket.has(normalizeOperationalStatus(status).toLowerCase()) ? sum + count : sum
  ), 0);
};

const getOemCode = (oemName: string) => {
  const explicitCodes: Record<string, string> = {
    'bgauss': 'BG',
    'e-sprinto': 'ESP',
    'motovolt': 'MV',
    'battre': 'BAT',
    'ola': 'OLA',
  };
  const normalized = oemName.toLowerCase();
  if (explicitCodes[normalized]) return explicitCodes[normalized];

  return oemName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
};

const formatContributionPercent = (value: number, total: number, digits = 1) => {
  if (!total || total <= 0) return '0%';
  return `${((value / total) * 100).toFixed(digits)}%`;
};

const formatFullDataNumber = (value: number, maximumFractionDigits = 0) =>
  value.toLocaleString('en-IN', { maximumFractionDigits });

const FULL_DATA_ORDER_RATES = [
  ['Amazon', 'Rs 40'],
  ['BB Now', 'Rs 47'],
  ['Blinkit', 'Rs 53'],
  ['DocPharma', 'Rs 140'],
  ['Flipkart Minutes', 'Rs 49'],
  ['Flipkart-LMA', 'Rs 18'],
  ['Inamo', 'Rs 65'],
  ['Instamart / Swiggy', 'Rs 49'],
  ['KPN', 'Rs 63'],
  ['KWIK Myntra', 'Rs 82'],
  ['KWIK Nykaa', 'Rs 80'],
  ['KWIK Purple', 'Rs 47'],
  ['Licious', 'Rs 56'],
  ['Rapido Ownly', 'Rs 90'],
  ['RSM', 'Rs 64'],
  ['Zepto', 'Rs 43'],
];

const FULL_DATA_MF_RATES = [
  ['Amazon', '8%'],
  ['BB / BB Now', '5%', '6% in Bengaluru, Chennai, Hyderabad, Mumbai'],
  ['Blinkit', '5%'],
  ['Flipkart-LMA', '3.3%'],
  ['Flipkart Minutes', '6%'],
  ['KPN', '5%'],
  ['KWIK', '5.5%'],
  ['KWIK Myntra', '5.5%'],
  ['KWIK Purple', '5.5%'],
  ['Licious', '6%'],
  ['RSM', '6%'],
  ['Zepto', '4%'],
  ['Rapido Ownly', '5%'],
  ['Inamo', '6%'],
  ['Swiggy / Instamart', '8%'],
];

const getOpsPanelSx = (theme: any) => ({
  mb: 3,
  borderRadius: 2,
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  bgcolor: 'background.paper',
  color: 'text.primary',
  boxShadow:
    theme.palette.mode === 'dark'
      ? `0 18px 44px ${alpha(theme.palette.common.black, 0.34)}`
      : `0 16px 36px ${alpha(theme.palette.grey[700], 0.14)}`,
});

const getOpsHeaderSx = (theme: any) => ({
  px: { xs: 1.5, md: 2 },
  py: 1.5,
  display: 'flex',
  alignItems: { xs: 'stretch', sm: 'center' },
  justifyContent: 'space-between',
  gap: 1.25,
  flexDirection: { xs: 'column', sm: 'row' },
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  bgcolor: theme.palette.mode === 'dark'
    ? '#17233b'
    : alpha(theme.palette.primary.main, 0.08),
});

const getReportTitleBarSx = (theme: any) => ({
  px: { xs: 1.25, md: 2 },
  py: 1.1,
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto' },
  alignItems: 'center',
  gap: { xs: 1, md: 2 },
  bgcolor: theme.palette.warning.main,
  color: theme.palette.common.black,
  borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.35)}`,
});

const getReportPillSx = (theme: any) => ({
  px: 1.35,
  py: 0.55,
  borderRadius: 1,
  bgcolor: theme.palette.mode === 'dark' ? '#020617' : '#061126',
  color: '#00ffc8',
  fontFamily: 'monospace',
  fontWeight: 950,
  textTransform: 'uppercase',
  letterSpacing: 0,
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

const getOpsTableSx = () => ({
  borderCollapse: 'separate',
  borderSpacing: 0,
  '& .MuiTableCell-root': {
    borderColor: 'divider',
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    fontWeight: 950,
    py: 1.25,
    letterSpacing: 0,
    lineHeight: 1.25,
  },
  '& .MuiTypography-root, & .MuiChip-root': {
    fontFamily: 'monospace',
    fontWeight: 950,
    letterSpacing: 0,
  },
  '& thead .MuiTableCell-root': {
    fontWeight: 950,
    textTransform: 'uppercase',
    letterSpacing: 0,
    bgcolor: (theme: any) => theme.palette.mode === 'dark'
      ? '#111827'
      : alpha(theme.palette.background.default, 0.96),
    color: 'text.primary',
  },
  '& tfoot .MuiTableCell-root': {
    fontWeight: 950,
    bgcolor: (theme: any) => theme.palette.mode === 'dark'
      ? '#0f172a'
      : alpha(theme.palette.background.default, 0.92),
  },
});

const getOpsSelectSx = (theme: any) => ({
  minWidth: { xs: '100%', sm: 220 },
  '& .MuiInputLabel-root': {
    color: 'text.secondary',
    fontWeight: 900,
  },
  '& .MuiInputBase-root': {
    minHeight: 40,
    borderRadius: 1.5,
    bgcolor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.black, 0.22)
      : alpha(theme.palette.background.paper, 0.86),
    fontWeight: 900,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
});

const getSelectedHighlightSx = (theme: any) => ({
  bgcolor: theme.palette.mode === 'dark'
    ? `${alpha(theme.palette.warning.main, 0.24)} !important`
    : `${alpha(theme.palette.warning.main, 0.2)} !important`,
  color: `${theme.palette.warning.main} !important`,
  boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.warning.main, 0.55)}`,
});

type OperationalStatusGroup = keyof typeof OPERATIONAL_STATUS_GROUP_COLORS;

const getOperationalGroupPalette = (group: OperationalStatusGroup) => {
  if (group === 'uptime') return 'success';
  if (group === 'downtime') return 'secondary';
  return 'error';
};

const getOperationalStatusCellSx = (theme: any, status: string) => {
  const group = getOperationalStatusGroup(status);
  const paletteKey = getOperationalGroupPalette(group);

  return {
    color: `${theme.palette[paletteKey].main} !important`,
    bgcolor: `${alpha(theme.palette[paletteKey].main, theme.palette.mode === 'dark' ? 0.15 : 0.08)} !important`,
  };
};

const getOperationalStatusGroup = (status: string): OperationalStatusGroup => {
  const normalizedStatus = normalizeOperationalStatus(status);
  if (UPTIME_OPERATIONAL_STATUSES.includes(normalizedStatus)) return 'uptime';
  if (DOWNTIME_OPERATIONAL_STATUSES.includes(normalizedStatus)) return 'downtime';
  return 'scrap';
};

const getOperationalStatusColor = (status: string) =>
  OPERATIONAL_STATUS_GROUP_COLORS[getOperationalStatusGroup(status)];

const sortOperationalStatuses = <T extends { status: string }>(rows: T[]) =>
  [...rows].sort((a, b) => {
    const aStatus = normalizeOperationalStatus(a.status);
    const bStatus = normalizeOperationalStatus(b.status);
    const aIndex = OPERATIONAL_STATUS_DISPLAY_ORDER.indexOf(aStatus);
    const bIndex = OPERATIONAL_STATUS_DISPLAY_ORDER.indexOf(bStatus);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return aStatus.localeCompare(bStatus);
  });

const sortOperationalStatusNames = (statuses: string[]) =>
  [...statuses].sort((a, b) => {
    const aStatus = normalizeOperationalStatus(a);
    const bStatus = normalizeOperationalStatus(b);
    const aIndex = OPERATIONAL_STATUS_DISPLAY_ORDER.indexOf(aStatus);
    const bIndex = OPERATIONAL_STATUS_DISPLAY_ORDER.indexOf(bStatus);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return aStatus.localeCompare(bStatus);
  });

const normalizeStatusByCity = (statusByCity: any) => {
  if (!statusByCity?.rows?.length) return statusByCity;

  const mergedRows = new Map<string, any>();
  statusByCity.rows.forEach((row: any) => {
    const canonicalStatus = normalizeOperationalStatus(row.status);
    const existing = mergedRows.get(canonicalStatus);
    const counts = Array.isArray(row.counts) ? row.counts : [];

    if (!existing) {
      mergedRows.set(canonicalStatus, {
        ...row,
        status: canonicalStatus,
        counts: [...counts],
        total: Number(row.total) || counts.reduce((sum: number, count: number) => sum + (Number(count) || 0), 0),
      });
      return;
    }

    const maxLength = Math.max(existing.counts.length, counts.length);
    existing.counts = Array.from({ length: maxLength }, (_, index) => (
      (Number(existing.counts[index]) || 0) + (Number(counts[index]) || 0)
    ));
    existing.total = (Number(existing.total) || 0)
      + (Number(row.total) || counts.reduce((sum: number, count: number) => sum + (Number(count) || 0), 0));
  });

  const grandTotal = Number(statusByCity.grandTotal) || Array.from(mergedRows.values()).reduce((sum, row) => sum + (Number(row.total) || 0), 0);
  const rows = Array.from(mergedRows.values()).map((row) => ({
    ...row,
    percentage: grandTotal > 0 ? ((Number(row.total) || 0) / grandTotal) * 100 : 0,
  }));

  return {
    ...statusByCity,
    rows,
    grandTotal,
  };
};

const normalizeOemStatusCounts = (counts: Record<string, Record<string, number>>) => (
  Object.entries(counts || {}).reduce<Record<string, Record<string, number>>>((acc, [oemName, statusCounts]) => {
    acc[oemName] = Object.entries(statusCounts || {}).reduce<Record<string, number>>((statusAcc, [status, count]) => {
      const canonicalStatus = normalizeOperationalStatus(status);
      statusAcc[canonicalStatus] = (statusAcc[canonicalStatus] || 0) + (Number(count) || 0);
      return statusAcc;
    }, {});
    return acc;
  }, {})
);

// Chart Component
// const VehicleChart: React.FC<{ 
//   data: Array<{ label: string; value: number; color?: string }>
//   title: string
//   subtitle?: string
// }> = ({ data, title, subtitle }) => {
//   const maxValue = Math.max(...data.map(item => item.value))
  
//   return (
//     <Card>
//       <CardHeader 
//         title={title}
//         subheader={subtitle}
//         titleTypographyProps={{ variant: 'h6', fontSize: '1rem' }}
//         avatar={<PieChartIcon color="primary" />}
//       />
//       <CardContent>
//         {data.length === 0 ? (
//           <Typography variant="body2" color="textSecondary" textAlign="center">
//             No data available
//           </Typography>
//         ) : (
//           data.map((item, index) => (
//             <Box key={index} mb={2}>
//               <Box display="flex" justifyContent="space-between" mb={0.5}>
//                 <Typography variant="body2" color="textSecondary">
//                   {item.label}
//                 </Typography>
//                 <Typography variant="body2" fontWeight="medium">
//                   {item.value.toLocaleString()}
//                 </Typography>
//               </Box>
//               <LinearProgress
//                 variant="determinate"
//                 value={maxValue > 0 ? (item.value / maxValue) * 100 : 0}
//                 sx={{
//                   height: 8,
//                   borderRadius: 4,
//                   backgroundColor: 'grey.200',
//                   '& .MuiLinearProgress-bar': {
//                     backgroundColor: item.color || 'primary.main',
//                     borderRadius: 4,
//                   },
//                 }}
//               />
//             </Box>
//           ))
//         )}
//       </CardContent>
//     </Card>
//   )
// }

const VehicleDashboard: React.FC = () => {
  const [timeRange, _setTimeRange] = useState('month')
  const [selectedHub, _setSelectedHub] = useState('')
  const [selectedStatusCity, setSelectedStatusCity] = useState('')
  const [selectedOem, setSelectedOem] = useState('')
  const today = useMemo(() => new Date(), [])
  const monthOptions = useMemo(() => getRecentMonthOptions(today), [today])
  const [fullDataMonth, setFullDataMonth] = useState(getMonthKey(today))
  const [fullDataCity, setFullDataCity] = useState('')
  const [fullDataClient, setFullDataClient] = useState('')
  const [showFullDataRates, setShowFullDataRates] = useState(false)
  const fullDataRange = useMemo(() => getMonthRange(fullDataMonth, today), [fullDataMonth, today])
  const fullDataLedgerEndDate = useMemo(() => toInputDate(today), [today])
  const deploymentStartDate = useMemo(() => toInputDate(addDays(today, -6)), [today])
  const deploymentEndDate = useMemo(() => toInputDate(today), [today])

  const deploymentDates = useMemo(() => {
    const dates: string[] = [];
    const start = new Date(`${deploymentStartDate}T00:00:00`);
    const end = new Date(`${deploymentEndDate}T00:00:00`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
      return dates;
    }

    for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
      dates.push(toInputDate(cursor));
    }

    return dates;
  }, [deploymentStartDate, deploymentEndDate])

  const fullDataDates = useMemo(() => {
    const dates: string[] = [];
    const start = new Date(`${fullDataRange.dateFrom}T00:00:00`);
    const end = new Date(`${fullDataRange.dateTo}T00:00:00`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
      return dates;
    }

    for (const cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
      dates.push(toInputDate(cursor));
    }

    return dates;
  }, [fullDataRange])

  // Fetch vehicle analytics
  const {
    data: vehicleAnalyticsData,
    error: vehicleAnalyticsError,
    refetch: refetchVehicleAnalytics,
  } = useQuery(
    ['vehicle-analytics', timeRange, selectedHub],
    () => vehicleService.getVehicleAnalytics(timeRange, selectedHub || undefined),
    {
      refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  )

  // Fetch service analytics
  const {
    data: serviceAnalyticsData,
  } = useQuery(
    ['service-analytics', timeRange],
    () => vehicleService.getServiceAnalytics(timeRange),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  )

  // Fetch damage analytics
  const {
    data: damageAnalyticsData,
  } = useQuery(
    ['damage-analytics', timeRange],
    () => vehicleService.getDamageAnalytics(timeRange),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  )

  // Fetch fleet performance
  const {
    data: fleetPerformanceData,
  } = useQuery(
    ['fleet-performance', timeRange],
    () => vehicleService.getFleetPerformance(timeRange),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  )

  const { data: underMaintenanceData } = useQuery(
    ['under-maintenance'],
    () => vehicleService.getUnderMaintenance(),
    {
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
    }
  )

  const [modelMatrixOem, setModelMatrixOem] = useState('');
  const [modelMatrixModel, setModelMatrixModel] = useState('');
  const {
    data: oemModelStatusData,
    isLoading: oemModelStatusLoading,
    isFetching: oemModelStatusFetching,
    error: oemModelStatusError,
    refetch: refetchOemModelStatus,
  } = useQuery(
    ['oem-model-operational-status-count'],
    () => vehicleService.getOemModelOperationalStatusCount(),
    { staleTime: 5 * 60 * 1000, refetchInterval: 5 * 60 * 1000 },
  );
  const modelMatrix = useMemo(() => buildOemModelStatusMatrix(
    oemModelStatusData?.data.rows || [], modelMatrixOem, modelMatrixModel, normalizeOperationalStatus,
  ), [oemModelStatusData, modelMatrixOem, modelMatrixModel]);

  // OEM operational status count
  const { data: oemStatusData, isLoading: oemStatusLoading } = useQuery(
    ['oem-operational-status-count'],
    () => vehicleService.getOemOperationalStatusCount(),
    {
      staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
    }
  );

  const {
    data: deploymentLog,
    isLoading: deploymentLogLoading,
    isFetching: deploymentLogFetching,
    error: deploymentLogError,
    refetch: refetchDeploymentLog,
  } = useQuery(
    ['pan-india-net-deployment-log', deploymentStartDate, deploymentEndDate],
    async () => {
      const hubs = await hubService.getOperationalHubs();
      const hubLedgers = await Promise.all(
        hubs.map(async (hub: HubResponse) => {
          const response = await vehicleService.getStatusBackupLedger(
            deploymentStartDate,
            deploymentEndDate,
            { hubId: hub.id },
          );

          return {
            hub,
            ledger: response.data,
          };
        }),
      );

      const rows: HubDeploymentRow[] = hubLedgers.map(({ hub, ledger }) => {
        const values = deploymentDates.reduce<HubDeploymentRow['values']>((acc, date) => {
          const deployed = getDailyParameterValue(ledger, date, ['Assigned', 'Assigned - Team Use']);
          const returned = getDailyParameterValue(ledger, date, ['Returned']);

          acc[date] = {
            deployed,
            return: returned,
            net: deployed - returned,
          };

          return acc;
        }, {});

        const totals = deploymentDates.reduce<HubDeploymentRow['totals']>((acc, date) => {
          acc.deployed += values[date]?.deployed || 0;
          acc.return += values[date]?.return || 0;
          acc.net += values[date]?.net || 0;
          return acc;
        }, { deployed: 0, return: 0, net: 0 });

        return {
          hubId: hub.id,
          hubName: `${hub.name || hub.code}${hub.code ? ` (${hub.code})` : ''}`,
          values,
          totals,
        };
      });

      const columnTotals = deploymentDates.reduce<Record<string, HubDeploymentRow['totals']>>((acc, date) => {
        acc[date] = rows.reduce<HubDeploymentRow['totals']>((sum, row) => {
          sum.deployed += row.values[date]?.deployed || 0;
          sum.return += row.values[date]?.return || 0;
          sum.net += row.values[date]?.net || 0;
          return sum;
        }, { deployed: 0, return: 0, net: 0 });

        return acc;
      }, {});

      const grandTotals = rows.reduce<HubDeploymentRow['totals']>((sum, row) => {
        sum.deployed += row.totals.deployed;
        sum.return += row.totals.return;
        sum.net += row.totals.net;
        return sum;
      }, { deployed: 0, return: 0, net: 0 });

      return {
        hubs,
        rows,
        columnTotals,
        grandTotals,
      };
    },
    {
      enabled: deploymentDates.length > 0,
      staleTime: 2 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
    },
  )

  const { data: fullDataCitiesResponse } = useQuery(
    ['full-data-vehicle-cities'],
    () => vehicleService.getCities({ isOperational: true }),
    {
      staleTime: 10 * 60 * 1000,
    },
  )

  const fullDataVehicleCityId = useMemo(() => {
    if (!fullDataCity) return undefined;
    const cities = fullDataCitiesResponse?.data || [];
    const selected = cities.find((city: any) => (
      String(city.name || '').toLowerCase() === fullDataCity.toLowerCase()
      || String(city.code || '').toLowerCase() === fullDataCity.toLowerCase()
    ));
    return selected?.id;
  }, [fullDataCitiesResponse, fullDataCity])

  const {
    data: fullDataVehicleLedger,
    isFetching: fullDataVehicleFetching,
    refetch: refetchFullDataVehicleLedger,
  } = useQuery(
    ['full-data-vehicle-ledger', fullDataRange.dateFrom, fullDataLedgerEndDate, fullDataVehicleCityId],
    () => vehicleService.getStatusBackupLedger(
      fullDataRange.dateFrom,
      fullDataLedgerEndDate,
      fullDataVehicleCityId ? { cityId: fullDataVehicleCityId } : undefined,
    ),
    {
      enabled: fullDataDates.length > 0 && (!fullDataCity || Boolean(fullDataVehicleCityId)),
      staleTime: 2 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
    },
  )

  const {
    data: fullDataRiderOrders,
    isFetching: fullDataRiderOrdersFetching,
    error: fullDataRiderOrdersError,
    refetch: refetchFullDataRiderOrders,
  } = useQuery(
    ['full-data-rider-orders', fullDataRange.dateFrom, fullDataRange.dateTo, fullDataCity, fullDataClient],
    () => ordersService.getFullDataAnalytics({
      dateFrom: fullDataRange.dateFrom,
      dateTo: fullDataRange.dateTo,
      city: fullDataCity || undefined,
      clientName: fullDataClient || undefined,
    }),
    {
      enabled: fullDataDates.length > 0,
      staleTime: 2 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000,
    },
  )

  const rawVehicleAnalytics = vehicleAnalyticsData?.data || {}
  const vehicleAnalytics = useMemo(() => ({
    ...rawVehicleAnalytics,
    statusByCity: normalizeStatusByCity(rawVehicleAnalytics.statusByCity),
  }), [rawVehicleAnalytics])
  const serviceAnalytics = serviceAnalyticsData?.data || {}
  const damageAnalytics = damageAnalyticsData?.data || {}
  const fleetPerformance = fleetPerformanceData?.data || {}

  const underMaintenanceCount = underMaintenanceData?.data
    ? Array.isArray(underMaintenanceData.data)
      ? underMaintenanceData.data.length
      : (typeof underMaintenanceData.data === 'number' ? underMaintenanceData.data : (underMaintenanceData.data.count ?? 0))
    : undefined

  const oemStatusCounts: Record<string, Record<string, number>> = useMemo(
    () => normalizeOemStatusCounts(oemStatusData?.data || {}),
    [oemStatusData?.data],
  );

  const totalTrackedDays = deploymentDates.length;


useEffect(() => {
  if (vehicleAnalytics?.summary) {
    // ✅ Dispatch event whenever vehicle stats change including latest underMaintenance
    window.dispatchEvent(
      new CustomEvent("vehicleStatsUpdated", {
        detail: {
          ...vehicleAnalytics.summary,
          underMaintenance: typeof underMaintenanceCount === 'number' ? underMaintenanceCount : vehicleAnalytics.summary?.underMaintenance
        }
      })
    )
  }
}, [vehicleAnalytics, underMaintenanceCount])

  const renderFullDataRatesTable = () => {
    const renderRateRows = (rows: string[][]) => rows.map(([client, rate, helper]) => (
      <Box
        key={`${client}-${rate}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 92px',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.9,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.15 }}>
            {client}
          </Typography>
          {helper && (
            <Typography color="text.secondary" sx={{ display: 'block', fontWeight: 850, fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.15 }}>
              {helper}
            </Typography>
          )}
        </Box>
        <Typography align="right" sx={{ fontWeight: 950, fontFamily: 'monospace', color: 'text.primary' }}>
          {rate}
        </Typography>
      </Box>
    ));

    const renderRatePanel = (title: string, valueHeader: string, rows: string[][]) => (
      <Box>
        <Typography sx={{ mb: 0.7, fontWeight: 950, fontFamily: 'monospace' }}>{title}</Typography>
        <Box sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}>
          <Box
            sx={(theme) => ({
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 92px',
              gap: 1,
              px: 1.25,
              py: 0.9,
              bgcolor: theme.palette.mode === 'dark'
                ? '#1f2937'
                : alpha(theme.palette.background.default, 0.96),
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontFamily: 'monospace',
              fontWeight: 950,
              fontSize: '0.78rem',
            })}
          >
            <Box>Client</Box>
            <Box sx={{ textAlign: 'right' }}>{valueHeader}</Box>
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto', overflowX: 'hidden' }}>
            {renderRateRows(rows)}
          </Box>
        </Box>
      </Box>
    );

    return (
      <Box sx={(theme) => ({
        mx: { xs: 1.25, md: 1.5 },
        mb: { xs: 1.25, md: 1.5 },
        mt: 0,
        p: { xs: 1.25, md: 1.5 },
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        bgcolor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.default, 0.72)
          : alpha(theme.palette.background.paper, 0.9),
      })}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
          <AnalyticsIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          <Typography sx={{ fontWeight: 950, fontFamily: 'monospace' }}>
            Earning / MF / Rent rates
          </Typography>
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 1.25, fontWeight: 800, fontFamily: 'monospace', fontSize: '0.76rem' }}>
          Earning = Orders x per-order rate. MF = Earning x MF%. Rent = on-road deployed vehicles x configured city/model rent. Revenue = Earning + MF + Rent.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            {renderRatePanel('Per-order rate (Rs)', 'Rate', FULL_DATA_ORDER_RATES)}
          </Grid>
          <Grid item xs={12} md={6}>
            {renderRatePanel('MF %', 'MF %', FULL_DATA_MF_RATES)}
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderFullDataReport = () => {
    const riderOrderData = fullDataRiderOrders?.data;
    const riderDailyMap = new Map<string, FullDataDailyRow>(
      (riderOrderData?.daily || []).map((row) => [row.date, row]),
    );
    const vehicleLedger = fullDataVehicleLedger?.data;
    const cityOptions = Array.from(new Set([
      ...(riderOrderData?.filters?.cities || []),
      ...((fullDataCitiesResponse?.data || []).map((city: any) => city.name).filter(Boolean)),
    ])).sort((a, b) => a.localeCompare(b));
    const clientOptions = Array.from(new Set(riderOrderData?.filters?.clients || []))
      .sort((a, b) => a.localeCompare(b));
    const isLoading = fullDataVehicleFetching || fullDataRiderOrdersFetching;
    const emptyVehicleLedger = { dailyReport: [] } as VehicleStatusBackupLedger;
    const getRiderDeployMovement = (date: string) =>
      getDailyParameterValue(vehicleLedger || emptyVehicleLedger, date, ['Assigned']);
    const getDeployMovement = (date: string) =>
      getDailyParameterValue(vehicleLedger || emptyVehicleLedger, date, ['Assigned', 'Assigned - Team Use']);
    const getReturnMovement = (date: string) =>
      getDailyParameterValue(vehicleLedger || emptyVehicleLedger, date, ['Returned']);
    const statusByCity = vehicleAnalytics.statusByCity;
    const selectedCityIndex = statusByCity?.cities?.findIndex((city: string) => (
      city.toLowerCase() === fullDataCity.toLowerCase()
    )) ?? -1;
    const getCurrentStatusTotal = (statuses: string[]) => {
      const normalizedStatuses = statuses.map((status) => normalizeOperationalStatus(status).toLowerCase());

      if (statusByCity?.rows?.length) {
        return statusByCity.rows.reduce((sum: number, row: any) => {
          if (!normalizedStatuses.includes(normalizeOperationalStatus(row.status).toLowerCase())) return sum;
          if (fullDataCity && selectedCityIndex >= 0) return sum + (row.counts?.[selectedCityIndex] || 0);
          if (fullDataCity && selectedCityIndex < 0) return sum;
          return sum + (row.total || 0);
        }, 0);
      }

      if (!fullDataCity) {
        return (vehicleAnalytics.summary?.assignedVehicles || 0)
          + (vehicleAnalytics.statusDistribution || []).reduce((sum: number, row: any) => (
            normalizedStatuses.includes(normalizeOperationalStatus(row.status).toLowerCase())
              && normalizeOperationalStatus(row.status).toLowerCase() !== 'assigned'
              ? sum + (Number(row.count) || 0)
              : sum
          ), 0);
      }

      return 0;
    };
    const currentDeployedTotal = getCurrentStatusTotal(['Assigned']);
    const getTotalDeployedForDate = (date: string) => {
      const futureNetMovement = fullDataDates
        .filter((candidate) => candidate > date)
        .reduce((sum, candidate) => sum + getRiderDeployMovement(candidate) - getReturnMovement(candidate), 0);
      const todayNetMovement = fullDataLedgerEndDate > fullDataRange.dateTo
        ? getRiderDeployMovement(fullDataLedgerEndDate) - getReturnMovement(fullDataLedgerEndDate)
        : 0;

      return Math.max(0, currentDeployedTotal - futureNetMovement - todayNetMovement);
    };

    const rows = [
      {
        section: 'Supply',
        label: 'Total Deployed Count',
        getValue: getTotalDeployedForDate,
        getTotal: () => currentDeployedTotal,
      },
      {
        section: 'Supply',
        label: 'Deployee Count',
        getValue: getDeployMovement,
      },
      {
        section: 'Supply',
        label: 'Return Count',
        getValue: getReturnMovement,
      },
      {
        section: 'Supply',
        label: 'Rider Count',
        getValue: (date: string) => riderDailyMap.get(date)?.riderCount || 0,
      },
      {
        section: 'Supply',
        label: 'EV rider Count',
        getValue: (date: string) => riderDailyMap.get(date)?.evRiderCount || 0,
      },
      {
        section: 'Supply',
        label: 'Non-EV rider Count',
        getValue: (date: string) => riderDailyMap.get(date)?.nonEvRiderCount || 0,
      },
      {
        section: 'Supply',
        label: 'Total Order',
        getValue: (date: string) => riderDailyMap.get(date)?.totalOrders || 0,
      },
      {
        section: 'Supply',
        label: 'EV order',
        getValue: (date: string) => riderDailyMap.get(date)?.evOrders || 0,
      },
      {
        section: 'Supply',
        label: 'Non-EV Order',
        getValue: (date: string) => riderDailyMap.get(date)?.nonEvOrders || 0,
      },
      {
        section: 'Supply',
        label: '0 order Rider count',
        getValue: (date: string) => riderDailyMap.get(date)?.zeroOrderRiderCount || 0,
      },
      {
        section: 'Supply',
        label: 'Total Earing',
        getValue: (date: string) => riderDailyMap.get(date)?.totalEarning || 0,
      },
      {
        section: 'Supply',
        label: 'EV Earing',
        getValue: (date: string) => riderDailyMap.get(date)?.evEarning || 0,
      },
      {
        section: 'Supply',
        label: 'Non Earing',
        getValue: (date: string) => riderDailyMap.get(date)?.nonEvEarning || 0,
      },
      {
        section: 'Supply',
        label: 'Rent',
        getValue: (date: string) => riderDailyMap.get(date)?.rent || 0,
      },
      {
        section: 'Supply',
        label: 'MF Amount',
        getValue: (date: string) => riderDailyMap.get(date)?.mfAmount || 0,
      },
      {
        section: 'Supply',
        label: 'Total Revenue',
        getValue: (date: string) => riderDailyMap.get(date)?.totalRevenue || 0,
      },
    ];

    const getFullDataAccentColor = (theme: any) => theme.palette.primary.main;

    const getFullDataCellSx = (theme: any, index = 0, isTotal = false) => ({
      bgcolor: isTotal
        ? `${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)} !important`
        : `${alpha(theme.palette.background.paper, index % 2 === 0 ? 0.98 : 0.9)} !important`,
      color: isTotal ? 'primary.main' : 'text.primary',
      fontWeight: 950,
      borderColor: alpha(theme.palette.divider, 0.9),
    });

    const listColumnWidth = 260;
    const totalColumnWidth = 138;
    const dateColumnWidth = 146;

    const exportFullDataCsv = () => {
      const header = ['LIST', 'TOTAL', ...fullDataDates.map(formatFullDataHeaderDate)];
      const csvRows = [
        header,
        ...rows.map((row) => {
          const values = fullDataDates.map(row.getValue);
          return [
            row.label,
            row.getTotal ? row.getTotal() : values.reduce((sum, value) => sum + value, 0),
            ...values,
          ];
        }),
      ];
      const csv = csvRows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fleetpro-full-data-${fullDataRange.dateFrom}-${fullDataRange.dateTo}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    };

    return (
      <Paper elevation={0} sx={(theme) => ({
        ...getOpsPanelSx(theme),
        bgcolor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.98)
          : alpha(theme.palette.background.paper, 0.94),
      })}>
        <Box sx={{ p: { xs: 1.5, md: 2.25 }, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.15, fontFamily: 'monospace' }}>
                Full Data
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, fontWeight: 800, fontFamily: 'monospace' }}>
                Month-wise supply, rider-order, earnings, MF, rent, and revenue matrix through yesterday.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant={showFullDataRates ? 'contained' : 'outlined'}
                onClick={() => setShowFullDataRates((value) => !value)}
                sx={(theme) => ({
                  fontWeight: 950,
                  borderColor: alpha(theme.palette.primary.main, 0.45),
                  boxShadow: showFullDataRates
                    ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}`
                    : 'none',
                })}
              >
                Rates
              </Button>
              <Button
                variant="outlined"
                startIcon={isLoading ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />}
                onClick={() => {
                  refetchFullDataVehicleLedger();
                  refetchFullDataRiderOrders();
                }}
                disabled={isLoading}
                sx={{ fontWeight: 900 }}
              >
                Refresh
              </Button>
              <Button variant="outlined" onClick={exportFullDataCsv} sx={{ fontWeight: 900 }}>
                Export
              </Button>
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
            <FormControl size="small" sx={getOpsSelectSx}>
              <InputLabel>Month</InputLabel>
              <Select
                value={fullDataMonth}
                label="Month"
                onChange={(event) => setFullDataMonth(event.target.value)}
              >
                {monthOptions.map((month) => (
                  <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={getOpsSelectSx}>
              <InputLabel>City</InputLabel>
              <Select
                value={fullDataCity}
                label="City"
                onChange={(event) => setFullDataCity(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {cityOptions.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={getOpsSelectSx}>
              <InputLabel>Clients</InputLabel>
              <Select
                value={fullDataClient}
                label="Clients"
                onChange={(event) => setFullDataClient(event.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {clientOptions.map((client) => (
                  <MenuItem key={client} value={client}>{client}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Chip
              label={formatFullDataRangeLabel(fullDataRange.dateFrom, fullDataRange.dateTo)}
              sx={(theme) => ({
                height: 40,
                alignSelf: { xs: 'stretch', md: 'center' },
                justifyContent: 'center',
                fontWeight: 900,
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.background.default, 0.55),
              })}
            />
          </Stack>
        </Box>

        {showFullDataRates && renderFullDataRatesTable()}

        {fullDataRiderOrdersError && (
          <Alert severity="warning" sx={{ m: 1.5 }}>
            Rider/order full data failed to load. Please refresh once after rider-service deploy.
          </Alert>
        )}

        <TableContainer sx={(theme) => ({
          maxHeight: 560,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.default, 0.62)
            : alpha(theme.palette.background.paper, 0.74),
          '&::-webkit-scrollbar': { height: 10, width: 10 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.secondary, 0.35),
            borderRadius: 8,
          },
        })}>
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: listColumnWidth + totalColumnWidth + fullDataDates.length * dateColumnWidth,
              tableLayout: 'fixed',
              ...getOpsTableSx(),
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={(theme) => ({
                    position: 'sticky',
                    left: 0,
                    width: listColumnWidth,
                    minWidth: listColumnWidth,
                    maxWidth: listColumnWidth,
                    zIndex: 6,
                    bgcolor: `${theme.palette.background.paper} !important`,
                    color: 'text.primary',
                    borderRight: `1px solid ${theme.palette.divider}`,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  })}
                >
                  List
                </TableCell>
                <TableCell
                  align="right"
                  sx={(theme) => ({
                    width: totalColumnWidth,
                    minWidth: totalColumnWidth,
                    maxWidth: totalColumnWidth,
                    zIndex: 4,
                    bgcolor: `${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.18) : alpha(theme.palette.primary.main, 0.08)} !important`,
                    color: 'primary.main',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  })}
                >
                  Total
                </TableCell>
                {fullDataDates.map((date) => (
                  <TableCell
                    key={date}
                    align="right"
                    sx={(theme) => ({
                      width: dateColumnWidth,
                      minWidth: dateColumnWidth,
                      maxWidth: dateColumnWidth,
                      zIndex: 2,
                      bgcolor: `${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.16) : alpha(theme.palette.primary.main, 0.06)} !important`,
                      color: 'text.primary',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    })}
                  >
                    {formatFullDataHeaderDate(date)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const values = fullDataDates.map(row.getValue);
                const total = row.getTotal ? row.getTotal() : values.reduce((sum, value) => sum + value, 0);
                return (
                  <TableRow
                    key={row.label}
                    hover
                    sx={(theme) => ({
                      '&:hover .MuiTableCell-root': {
                        bgcolor: `${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1)} !important`,
                      },
                    })}
                  >
                    <TableCell sx={(theme) => ({
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      width: listColumnWidth,
                      minWidth: listColumnWidth,
                      maxWidth: listColumnWidth,
                      fontWeight: 950,
                      borderLeft: `4px solid ${getFullDataAccentColor(theme)}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...getFullDataCellSx(theme, 0),
                    })}>
                      {row.label}
                    </TableCell>
                    <TableCell align="right" sx={(theme) => ({
                      width: totalColumnWidth,
                      minWidth: totalColumnWidth,
                      maxWidth: totalColumnWidth,
                      fontWeight: 950,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...getFullDataCellSx(theme, 0, true),
                    })}>
                      {formatFullDataNumber(total)}
                    </TableCell>
                    {values.map((value, index) => (
                      <TableCell
                        key={`${row.label}-${fullDataDates[index]}`}
                        align="right"
                        sx={(theme) => ({
                          width: dateColumnWidth,
                          minWidth: dateColumnWidth,
                          maxWidth: dateColumnWidth,
                          fontWeight: 950,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          ...getFullDataCellSx(theme, index),
                        })}
                      >
                        {formatFullDataNumber(value)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderDeploymentLog = () => {
    const sortedDeploymentRows = [...(deploymentLog?.rows || [])].sort((a, b) => {
      const aActivity = a.totals.deployed + a.totals.return;
      const bActivity = b.totals.deployed + b.totals.return;

      if (aActivity !== bActivity) return bActivity - aActivity;
      if (a.totals.net !== b.totals.net) return b.totals.net - a.totals.net;
      return a.hubName.localeCompare(b.hubName);
    });
    const activeHubCount = sortedDeploymentRows.filter((row) => row.totals.deployed + row.totals.return > 0).length;
    const getMetricTextColor = (theme: any, metric: DeploymentMetricKey) => {
      if (metric === 'deployed') return theme.palette.success.main;
      if (metric === 'return') return theme.palette.error.main;
      return theme.palette.warning.main;
    };
    const getMetricCellBg = (theme: any, metric: DeploymentMetricKey) => {
      if (metric === 'deployed') return alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.14 : 0.08);
      if (metric === 'return') return alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.08);
      return alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.1);
    };

    return (
    <Paper
      elevation={0}
      sx={getOpsPanelSx}
    >
      <Box
        sx={getOpsHeaderSx}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <AnalyticsIcon sx={{ fontSize: 17, color: 'primary.main' }} />
            <Typography
              sx={{
                color: 'text.primary',
                fontWeight: 950,
                fontSize: { xs: '0.82rem', md: '0.95rem' },
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              1. Pan India Net Deployment Trends
            </Typography>
          </Stack>
          <Typography
            sx={{
              mt: 0.75,
              color: 'text.secondary',
              fontSize: '0.72rem',
              fontFamily: 'monospace',
            }}
          >
            Formula:{' '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 900 }}>Deployed</Box>
            {' - '}
            <Box component="span" sx={{ color: 'error.main', fontWeight: 900 }}>Return</Box>
            {' = '}
            <Box component="span" sx={{ color: 'warning.main', fontWeight: 900 }}>Net Deployment</Box>
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              color: 'primary.main',
              fontSize: '0.72rem',
              fontFamily: 'monospace',
              fontWeight: 900,
              textTransform: 'uppercase',
            }}
          >
            Latest 7 Days Trend
          </Typography>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={(theme) => ({
            '& .MuiInputBase-root': {
              height: 38,
              color: 'text.primary',
              backgroundColor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.55 : 0.85),
              borderRadius: 1,
              fontWeight: 800,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: alpha(theme.palette.divider, 0.9),
            },
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              fontWeight: 800,
            },
            '& input': {
              colorScheme: theme.palette.mode,
            },
          })}
        >
          <Button
            variant="outlined"
            startIcon={deploymentLogFetching ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />}
            onClick={() => refetchDeploymentLog()}
            disabled={deploymentLogFetching}
            sx={(theme) => ({
              height: 38,
              color: 'primary.main',
              borderColor: alpha(theme.palette.primary.main, 0.45),
              fontWeight: 900,
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            })}
          >
            Refresh
          </Button>
          <Chip
            label={`${activeHubCount} Active Hubs | 7 Days Trend`}
            sx={(theme) => ({
              height: 38,
              color: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
              fontWeight: 950,
            })}
          />
        </Stack>
      </Box>

      {deploymentLogError && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Failed to load Pan India deployment log. Please retry.
        </Alert>
      )}

      <TableContainer
        sx={(theme) => ({
          maxHeight: '76vh',
          overflow: 'auto',
          background: theme.palette.background.paper,
          '&::-webkit-scrollbar': { height: 10, width: 10 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.secondary, 0.35), borderRadius: 8 },
        })}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 360 + Math.max(totalTrackedDays, 1) * 112 + 100,
            tableLayout: 'fixed',
            ...getOpsTableSx(),
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 8,
                  width: 118,
                  minWidth: 118,
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : alpha(theme.palette.background.default, 0.96),
                  color: 'warning.main',
                  fontWeight: 950,
                  textTransform: 'uppercase',
                }}
              >
                Hub / Warehouse
              </TableCell>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 118,
                  zIndex: 8,
                  width: 164,
                  minWidth: 164,
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : alpha(theme.palette.background.default, 0.96),
                  color: 'primary.main',
                  fontWeight: 950,
                  textTransform: 'uppercase',
                }}
              >
                Deployment<br />Metric
              </TableCell>
              {deploymentDates.map((date) => (
                <TableCell
                  key={date}
                  align="center"
                  sx={{
                    width: 112,
                    minWidth: 112,
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? theme.palette.background.paper
                      : alpha(theme.palette.background.default, 0.96),
                    color: 'text.primary',
                    fontWeight: 950,
                  }}
                >
                  {formatLogHeaderDate(date)}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  position: 'sticky',
                  right: 0,
                  zIndex: 8,
                  width: 92,
                  minWidth: 100,
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.background.paper
                    : alpha(theme.palette.background.default, 0.96),
                  color: 'success.main',
                  fontWeight: 950,
                  textTransform: 'uppercase',
                }}
              >
                Total<br />Period
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {deploymentLogLoading && (
              <TableRow>
                <TableCell
                  colSpan={deploymentDates.length + 3}
                  align="center"
                  sx={{ py: 7, backgroundColor: 'background.paper', color: 'text.primary' }}
                >
                  <CircularProgress size={28} color="primary" />
                  <Typography sx={{ mt: 1.5, fontWeight: 800, fontSize: '0.78rem' }}>
                    Loading hub deployment movement...
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!deploymentLogLoading && sortedDeploymentRows.map((hubRow) => (
              DEPLOYMENT_METRICS.map((metric, metricIndex) => (
                <TableRow key={`${hubRow.hubId}-${metric.key}`}>
                  {metricIndex === 0 && (
                    <TableCell
                      rowSpan={3}
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                        width: 118,
                        minWidth: 118,
                        backgroundColor: 'background.paper',
                        color: 'warning.main',
                        fontWeight: 950,
                        verticalAlign: 'middle',
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.12),
                          border: '1px solid',
                          borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {hubRow.hubName}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 118,
                      zIndex: 5,
                      width: 164,
                      minWidth: 164,
                      backgroundColor: 'background.paper',
                      color: (theme) => getMetricTextColor(theme, metric.key),
                      fontWeight: 950,
                      borderRight: '1px solid',
                      borderRightColor: 'divider',
                    }}
                  >
                    {metric.label}
                  </TableCell>
                  {deploymentDates.map((date) => (
                    <TableCell
                      key={`${hubRow.hubId}-${metric.key}-${date}`}
                      align="center"
                      sx={{
                        backgroundColor: (theme) => getMetricCellBg(theme, metric.key),
                        color: (theme) => getMetricTextColor(theme, metric.key),
                        fontWeight: 950,
                      }}
                    >
                      {hubRow.values[date]?.[metric.key] ?? 0}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      position: 'sticky',
                      right: 0,
                      zIndex: 5,
                      backgroundColor: 'background.paper',
                      color: (theme) => getMetricTextColor(theme, metric.key),
                      fontWeight: 950,
                    }}
                  >
                    {hubRow.totals[metric.key].toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            ))}

            {!deploymentLogLoading && sortedDeploymentRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={deploymentDates.length + 3}
                  align="center"
                  sx={{ py: 6, backgroundColor: 'background.paper', color: 'text.secondary', fontWeight: 800 }}
                >
                  No operational hub data available for this date range.
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {!deploymentLogLoading && deploymentLog && (
            <TableFooter>
              {DEPLOYMENT_METRICS.map((metric, metricIndex) => (
                <TableRow key={`pan-india-total-${metric.key}`}>
                  {metricIndex === 0 && (
                    <TableCell
                      rowSpan={3}
                      sx={{
                        position: 'sticky',
                        left: 0,
                        bottom: 0,
                        zIndex: 7,
                        backgroundColor: 'background.paper',
                        color: 'warning.main',
                        fontWeight: 950,
                        textTransform: 'uppercase',
                        borderTop: '1px solid',
                        borderTopColor: 'divider',
                      }}
                    >
                      Pan India<br />Total
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 118,
                      bottom: 0,
                      zIndex: 7,
                      backgroundColor: 'background.paper',
                      color: (theme) => getMetricTextColor(theme, metric.key),
                      fontWeight: 950,
                      borderTop: '1px solid',
                      borderTopColor: 'divider',
                    }}
                  >
                    {metric.label.replace(/^\d\.\s/, '')} Total
                  </TableCell>
                  {deploymentDates.map((date) => (
                    <TableCell
                      key={`total-${metric.key}-${date}`}
                      align="center"
                      sx={{
                        bottom: 0,
                        backgroundColor: (theme) => metric.key === 'net'
                          ? alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.1)
                          : alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.55 : 0.8),
                        color: (theme) => getMetricTextColor(theme, metric.key),
                        fontWeight: 950,
                        borderTop: '1px solid',
                        borderTopColor: 'divider',
                      }}
                    >
                      {deploymentLog.columnTotals[date]?.[metric.key] ?? 0}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      position: 'sticky',
                      right: 0,
                      bottom: 0,
                      zIndex: 7,
                      backgroundColor: 'background.paper',
                      color: (theme) => getMetricTextColor(theme, metric.key),
                      fontWeight: 950,
                      borderTop: '1px solid',
                      borderTopColor: 'divider',
                    }}
                  >
                    {deploymentLog.grandTotals[metric.key].toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  )
  }

  const renderOemModelStatusTable = () => {
    const source = oemModelStatusData?.data.rows || [];
    const oems = [...new Map(source.map((row) => [row.oemId, row.oemName])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1]));
    const models = source.filter((row) => !modelMatrixOem || row.oemId === modelMatrixOem);
    const statuses = sortOperationalStatusNames(Object.keys(modelMatrix.statusTotals));
    return (
      <Paper elevation={0} sx={getOpsPanelSx}>
        <Box sx={getReportTitleBarSx}>
          <Box sx={getReportPillSx}>Report 4: OEM ? Vehicle Model</Box>
          <Typography sx={{ fontWeight: 950, textTransform: 'uppercase', textAlign: 'center', fontSize: { xs: '0.86rem', md: '1rem' } }}>
            Pan India - Current Vehicle Status by OEM and Vehicle Model
          </Typography>
          <Box sx={getReportPillSx}>All Cities</Box>
        </Box>
        <Box sx={getOpsHeaderSx}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <OEMIcon color="primary" />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              {new Set(modelMatrix.rows.map((row) => row.oemId)).size} OEMs | {modelMatrix.rows.length} Models | {modelMatrix.grandTotal.toLocaleString()} Vehicles
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <FormControl size="small" sx={getOpsSelectSx}>
              <InputLabel id="model-matrix-oem-label">Select OEM</InputLabel>
              <Select labelId="model-matrix-oem-label" value={modelMatrixOem} label="Select OEM"
                onChange={(event) => { setModelMatrixOem(event.target.value); setModelMatrixModel(''); }}>
                <MenuItem value="">All OEMs</MenuItem>
                {oems.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={getOpsSelectSx}>
              <InputLabel id="model-matrix-model-label">Select Vehicle Model</InputLabel>
              <Select labelId="model-matrix-model-label" value={modelMatrixModel} label="Select Vehicle Model"
                onChange={(event) => setModelMatrixModel(event.target.value)}>
                <MenuItem value="">All Models</MenuItem>
                {models.map((row) => <MenuItem key={row.modelId} value={row.modelId}>{row.oemName} ? {row.modelName}</MenuItem>)}
              </Select>
            </FormControl>
            <IconButton aria-label="Refresh OEM and vehicle model counts" disabled={oemModelStatusFetching}
              onClick={() => refetchOemModelStatus()}><RefreshIcon /></IconButton>
          </Stack>
        </Box>
        {oemModelStatusError ? (
          <Alert severity="error" sx={{ m: 2 }}>Unable to load OEM and vehicle model counts. Please refresh to retry.</Alert>
        ) : oemModelStatusLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} aria-label="Loading OEM and vehicle model counts" /></Box>
        ) : !modelMatrix.rows.length ? (
          <Typography sx={{ p: 3 }} color="text.secondary">No vehicles found for the selected OEM and model.</Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', maxHeight: 650 }}>
            <Table size="small" stickyHeader aria-label="Vehicle status counts by OEM and vehicle model" sx={getOpsTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 140 }}>OEM</TableCell>
                  <TableCell sx={{ minWidth: 170 }}>Vehicle Model</TableCell>
                  {statuses.map((status) => <TableCell key={status} align="center" sx={{ minWidth: 125, maxWidth: 175 }}>{status}</TableCell>)}
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Fleet Share</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modelMatrix.rows.map((row) => (
                  <TableRow key={row.modelId} hover>
                    <TableCell>{row.oemName}</TableCell>
                    <TableCell>{row.modelName}</TableCell>
                    {statuses.map((status) => {
                      const count = row.statusCounts[status] || 0;
                      return <TableCell key={status} align="center" sx={(theme) => getOperationalStatusCellSx(theme, status)}>
                        {count.toLocaleString()}
                        <Typography component="div" variant="caption" color="text.secondary">({formatContributionPercent(count, row.total)})</Typography>
                      </TableCell>;
                    })}
                    <TableCell align="right">{row.total.toLocaleString()}</TableCell>
                    <TableCell align="right">{formatContributionPercent(row.total, modelMatrix.grandTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2}>Total</TableCell>
                  {statuses.map((status) => <TableCell key={status} align="center">
                    {modelMatrix.statusTotals[status].toLocaleString()}
                    <Typography component="div" variant="caption" color="text.secondary">({formatContributionPercent(modelMatrix.statusTotals[status], modelMatrix.grandTotal)})</Typography>
                  </TableCell>)}
                  <TableCell align="right">{modelMatrix.grandTotal.toLocaleString()}</TableCell>
                  <TableCell align="right">{formatContributionPercent(modelMatrix.grandTotal, modelMatrix.grandTotal)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" component="div" color="text.secondary" sx={{ px: 2, py: 1 }}>
          Status percentages use each model's total. Fleet share and footer percentages use the filtered fleet total. Scroll horizontally to view every status.
        </Typography>
      </Paper>
    );
  };

  // Render OEM operational status analytics card
  const renderOemStatusAnalyticsCard = () => {
    if (oemStatusLoading) return null;

    const preferredOemOrder = ['BGauss', 'E-Sprinto', 'Motovolt', 'BattRE', 'Ola'];
    const oemNames = Object.keys(oemStatusCounts).sort((a, b) => {
      const aIndex = preferredOemOrder.findIndex(
        (name) => name.toLowerCase() === a.toLowerCase(),
      );
      const bIndex = preferredOemOrder.findIndex(
        (name) => name.toLowerCase() === b.toLowerCase(),
      );

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    if (oemNames.length === 0) {
      return (
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
          }}
        >
          <CardHeader
            title="OEM Operational Status Analytics"
            avatar={<OEMIcon color="primary" />}
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
          />
          <CardContent>
            <Typography variant="body2" color="textSecondary">
              No data available
            </Typography>
          </CardContent>
        </Card>
      );
    }

    // Collect all unique statuses across all OEMs
    const allStatuses = new Set<string>();
    Object.values(oemStatusCounts).forEach(statusData => {
      Object.keys(statusData).forEach(status => allStatuses.add(status));
    });
    const statusList = sortOperationalStatusNames(Array.from(allStatuses));

    // Calculate totals per OEM and per status
    const oemTotals: Record<string, number> = {};
    const statusTotals: Record<string, number> = {};
    let grandTotal = 0;

    oemNames.forEach(oemName => {
      const statusData = oemStatusCounts[oemName];
      const total = Object.values(statusData).reduce((sum: number, count: number) => sum + count, 0);
      oemTotals[oemName] = total;
      grandTotal += total;

      statusList.forEach(status => {
        const count = statusData[status] || 0;
        statusTotals[status] = (statusTotals[status] || 0) + count;
      });
    });

    return (
      <Paper
        elevation={0}
        sx={getOpsPanelSx}
      >
        <Box sx={getReportTitleBarSx}>
          <Box sx={getReportPillSx}>Report 3: OEM Matrix</Box>
          <Typography
            sx={{
              fontWeight: 950,
              textTransform: 'uppercase',
              textAlign: 'center',
              fontSize: { xs: '0.86rem', md: '1rem' },
              lineHeight: 1.2,
              color: 'inherit',
            }}
          >
            Pan India - Current Vehicle Status Based On OEM (Overall Fleet (All Cities))
          </Typography>
          <Box sx={getReportPillSx}>{oemNames.length} OEMs</Box>
        </Box>
        <Box sx={getOpsHeaderSx}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <OEMIcon sx={{ color: 'primary.main', fontSize: 23 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              {statusList.length} Status Types | {grandTotal.toLocaleString()} Total Vehicles
            </Typography>
          </Stack>
          <FormControl size="small" sx={getOpsSelectSx}>
            <InputLabel>Select OEM</InputLabel>
            <Select
              value={selectedOem}
              label="Select OEM"
              onChange={(event) => setSelectedOem(event.target.value)}
            >
              <MenuItem value="">All OEMs</MenuItem>
              {oemNames.map((oemName) => (
                <MenuItem key={oemName} value={oemName}>{oemName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <CardContent sx={{ p: { xs: 1.25, md: 0 }, overflowX: { xs: 'hidden', md: 'auto' } }}>
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Stack spacing={1}>
              {statusList.map((status) => (
                <Box
                  key={status}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: `4px solid ${getOperationalStatusColor(status)}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={800}
                      sx={{ color: getOperationalStatusColor(status) }}
                    >
                      {status}
                    </Typography>
                    <Chip label={statusTotals[status]?.toLocaleString()} size="small" color="primary" variant="outlined" />
                  </Stack>
                  <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    {oemNames.map(oemName => {
                      const count = oemStatusCounts[oemName][status] || 0;
                      const oemTotal = oemTotals[oemName] || 0;
                      return (
                        <Grid item xs={6} key={`${status}-${oemName}-mobile`}>
                          <Box
                            sx={(theme) => ({
                              p: 0.8,
                              borderRadius: 1,
                              ...getOperationalStatusCellSx(theme, status),
                              ...(selectedOem === oemName ? getSelectedHighlightSx(theme) : {}),
                            })}
                          >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                            {oemName}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {count.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
                            ({formatContributionPercent(count, oemTotal)})
                          </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Stack>
          </Box>
          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table size="small" sx={getOpsTableSx()}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '' }}>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Status</TableCell>
                  {oemNames.map(oemName => (
                    <TableCell
                      key={oemName}
                      align="center"
                      sx={(theme) => ({
                        fontWeight: 'bold',
                        minWidth: 120,
                        borderLeft: '1px solid',
                        borderLeftColor: 'divider',
                        ...(selectedOem === oemName ? getSelectedHighlightSx(theme) : {}),
                      })}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {oemName}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ minWidth: 100 }}>
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statusList.map((status) => {
                  const statusTotal = statusTotals[status];

                  return (
                    <TableRow key={status} hover sx={{ backgroundColor: '' }}>
                      <TableCell 
                        sx={{ 
                         
                          borderLeft: `4px solid ${getOperationalStatusColor(status)}`,
                          color: getOperationalStatusColor(status),
                          fontWeight: 800,
                        }}
                      >
                        {status}
                      </TableCell>
                      {oemNames.map(oemName => {
                        const count = oemStatusCounts[oemName][status] || 0;
                        const contribution = formatContributionPercent(count, oemTotals[oemName] || 0);
                        
                        return (
                          <TableCell
                            key={`${status}-${oemName}`}
                            align="center"
                            sx={(theme) => ({
                              borderLeft: '1px solid',
                              borderLeftColor: 'divider',
                              ...getOperationalStatusCellSx(theme, status),
                              ...(selectedOem === oemName ? getSelectedHighlightSx(theme) : {}),
                            })}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 850, lineHeight: 1.05 }}>
                              {count.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.15 }}>
                              ({contribution})
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" sx={{ }}>
                        {statusTotal?.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals Row */}
                <TableRow sx={{ backgroundColor: '', borderTop: '2px solid grey.300' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                  {oemNames.map(oemName => (
                    <TableCell
                      key={`total-${oemName}`}
                      align="center"
                      sx={(theme) => ({
                        fontWeight: 'bold',
                        borderLeft: '1px solid',
                        borderLeftColor: 'divider',
                        ...(selectedOem === oemName ? getSelectedHighlightSx(theme) : {}),
                      })}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                        {oemTotals[oemName]?.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.15 }}>
                        ({formatContributionPercent(oemTotals[oemName] || 0, grandTotal)})
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {grandTotal.toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Paper>
    );
  };

  const renderOemFleetDistributionTable = () => {
    if (oemStatusLoading) return null;

    const preferredOemOrder = ['BGauss', 'E-Sprinto', 'Motovolt', 'BattRE', 'Ola'];
    const oemNames = Object.keys(oemStatusCounts).sort((a, b) => {
      const aIndex = preferredOemOrder.findIndex((name) => name.toLowerCase() === a.toLowerCase());
      const bIndex = preferredOemOrder.findIndex((name) => name.toLowerCase() === b.toLowerCase());

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    const oemColors = ['#10b981', '#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#64748b'];
    const rows = oemNames.map((oemName, index) => {
      const statusCounts = oemStatusCounts[oemName] || {};
      const totalVehicles = Object.values(statusCounts).reduce((sum, count) => sum + count, 0);
      const activeDeployed = getStatusBucketCount(statusCounts, DEPLOYED_STATUS_NAMES);
      const readyFleet = getStatusBucketCount(statusCounts, READY_FLEET_STATUS_NAMES);
      const legalScrap = getStatusBucketCount(statusCounts, LEGAL_SCRAP_STATUS_NAMES);
      const workshopMaintenance = Math.max(
        0,
        totalVehicles - activeDeployed - readyFleet - legalScrap,
      );
      const availability = totalVehicles > 0
        ? Math.round(((activeDeployed + readyFleet) / totalVehicles) * 100)
        : 0;
      const healthState = availability >= 80
        ? 'Healthy Fleet'
        : availability >= 70
          ? 'Moderate Downtime'
          : 'High Workshop Load';

      return {
        oemName,
        code: getOemCode(oemName),
        color: oemColors[index % oemColors.length],
        totalVehicles,
        activeDeployed,
        readyFleet,
        workshopMaintenance,
        legalScrap,
        availability,
        healthState,
      };
    });

    const totals = rows.reduce(
      (acc, row) => {
        acc.totalVehicles += row.totalVehicles;
        acc.activeDeployed += row.activeDeployed;
        acc.readyFleet += row.readyFleet;
        acc.workshopMaintenance += row.workshopMaintenance;
        acc.legalScrap += row.legalScrap;
        return acc;
      },
      {
        totalVehicles: 0,
        activeDeployed: 0,
        readyFleet: 0,
        workshopMaintenance: 0,
        legalScrap: 0,
      },
    );
    const totalAvailability = totals.totalVehicles > 0
      ? Math.round(((totals.activeDeployed + totals.readyFleet) / totals.totalVehicles) * 100)
      : 0;

    const getAvailabilityChipSx = (availability: number) => {
      if (availability >= 80) return { color: '#047857', bgcolor: '#bbf7d0' };
      if (availability >= 70) return { color: '#b45309', bgcolor: '#fde68a' };
      return { color: '#e11d48', bgcolor: '#ffe4e6' };
    };

    const getHealthChipSx = (healthState: string) => {
      if (healthState === 'Healthy Fleet') {
        return { color: '#047857', bgcolor: '#ecfdf5', borderColor: '#86efac' };
      }
      if (healthState === 'Moderate Downtime') {
        return { color: '#b45309', bgcolor: '#fff7ed', borderColor: '#fbbf24' };
      }
      return { color: '#e11d48', bgcolor: '#fff1f2', borderColor: '#fda4af' };
    };

    return (
      <Paper
        elevation={0}
        sx={getOpsPanelSx}
      >
        <Box
          sx={getOpsHeaderSx}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <MaintenanceIcon sx={{ color: 'primary.main', fontSize: 23 }} />
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: { xs: '0.92rem', md: '1rem' },
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              Table 2: OEM Brand Maintenance & Status Distribution - Overall Fleet (All Cities)
            </Typography>
          </Stack>
          <Chip
            label={`${rows.length} OEM Manufacturers`}
            sx={(theme) => ({
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
              fontWeight: 950,
              fontFamily: 'monospace',
            })}
          />
          {/* <FormControl size="small" sx={getOpsSelectSx}>
            <InputLabel>Select OEM</InputLabel>
            <Select
              value={selectedOem}
              label="Select OEM"
              onChange={(event) => setSelectedOem(event.target.value)}
            >
              <MenuItem value="">All OEMs</MenuItem>
              {oemNames.map((oemName) => (
                <MenuItem key={oemName} value={oemName}>{oemName}</MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 1120,
              ...getOpsTableSx(),
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 230 }}>OEM Manufacturer</TableCell>
                <TableCell align="center">Total<br />Vehicles</TableCell>
                <TableCell align="center" sx={(theme) => ({ color: `${theme.palette.success.main} !important`, bgcolor: `${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.13 : 0.08)} !important` })}>
                  Active<br />Deployed
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ color: `${theme.palette.primary.main} !important`, bgcolor: `${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.13 : 0.08)} !important` })}>
                  Ready<br />Fleet
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ color: `${theme.palette.warning.main} !important`, bgcolor: `${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.13 : 0.1)} !important` })}>
                  Workshop &<br />Maintenance
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ color: `${theme.palette.error.main} !important`, bgcolor: `${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.13 : 0.08)} !important` })}>
                  Legal / Scrap /<br />Missing
                </TableCell>
                <TableCell align="center">Operational<br />Availability</TableCell>
                <TableCell align="center">OEM Health State</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.oemName}
                  hover
                  sx={(theme) => selectedOem === row.oemName ? {
                    '& .MuiTableCell-root': getSelectedHighlightSx(theme),
                  } : {}}
                >
                  <TableCell sx={{ fontWeight: 900, color: 'text.primary' }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 15,
                          height: 15,
                          borderRadius: '50%',
                          bgcolor: row.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontWeight: 950, color: 'text.primary' }}>{row.oemName}</Typography>
                      <Chip
                        label={row.code}
                        size="small"
                        sx={(theme) => ({
                          height: 20,
                          color: 'text.secondary',
                          bgcolor: alpha(theme.palette.text.secondary, 0.1),
                          fontWeight: 950,
                          fontSize: '0.68rem',
                        })}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 950, color: 'text.primary', fontFamily: 'monospace', fontSize: '1rem' }}>
                    {row.totalVehicles.toLocaleString()}
                  </TableCell>
                  <TableCell align="center" sx={(theme) => ({ fontWeight: 950, color: 'success.main', bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.1 : 0.06), fontFamily: 'monospace' })}>
                    <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                      {row.activeDeployed.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                      ({formatContributionPercent(row.activeDeployed, row.totalVehicles)})
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={(theme) => ({ fontWeight: 950, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.06), fontFamily: 'monospace' })}>
                    <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                      {row.readyFleet.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                      ({formatContributionPercent(row.readyFleet, row.totalVehicles)})
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={(theme) => ({ fontWeight: 950, color: 'warning.main', bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.1 : 0.08), fontFamily: 'monospace' })}>
                    <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                      {row.workshopMaintenance.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                      ({formatContributionPercent(row.workshopMaintenance, row.totalVehicles)})
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={(theme) => ({ fontWeight: 950, color: 'error.main', bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.1 : 0.06), fontFamily: 'monospace' })}>
                    <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                      {row.legalScrap.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                      ({formatContributionPercent(row.legalScrap, row.totalVehicles)})
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${row.availability}%`}
                      sx={{
                        height: 30,
                        minWidth: 54,
                        fontWeight: 950,
                        ...getAvailabilityChipSx(row.availability),
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.healthState}
                      variant="outlined"
                      sx={{
                        height: 'auto',
                        minHeight: 28,
                        py: 0.25,
                        fontWeight: 950,
                        whiteSpace: 'normal',
                        maxWidth: 145,
                        ...getHealthChipSx(row.healthState),
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'text.primary', fontWeight: 950, textTransform: 'uppercase' })}>
                  OEM Summary<br />Totals:
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'warning.main', fontWeight: 950, fontFamily: 'monospace', fontSize: '1rem' })}>
                  {totals.totalVehicles.toLocaleString()}
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'success.main', fontWeight: 950, fontFamily: 'monospace' })}>
                  <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                    {totals.activeDeployed.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                    ({formatContributionPercent(totals.activeDeployed, totals.totalVehicles)})
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'primary.main', fontWeight: 950, fontFamily: 'monospace' })}>
                  <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                    {totals.readyFleet.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                    ({formatContributionPercent(totals.readyFleet, totals.totalVehicles)})
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'warning.main', fontWeight: 950, fontFamily: 'monospace' })}>
                  <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                    {totals.workshopMaintenance.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                    ({formatContributionPercent(totals.workshopMaintenance, totals.totalVehicles)})
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'error.main', fontWeight: 950, fontFamily: 'monospace' })}>
                  <Typography variant="body2" sx={{ fontWeight: 950, fontFamily: 'monospace', lineHeight: 1.05 }}>
                    {totals.legalScrap.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 850, lineHeight: 1.15 }}>
                    ({formatContributionPercent(totals.legalScrap, totals.totalVehicles)})
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'success.main', fontWeight: 950, fontFamily: 'monospace' })}>
                  {totalAvailability}%
                </TableCell>
                <TableCell align="center" sx={(theme) => ({ bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.7 : 0.92), color: 'text.secondary', fontWeight: 950 })}>
                  -
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    );
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const fleetCards = [
    {
      label: 'Total Fleet Size',
      value: (vehicleAnalytics.summary?.totalVehicles || 0).toLocaleString(),
      helper: 'All registered vehicles',
      icon: <VehicleIcon />,
      main: '#2563eb',
      soft: '#dbeafe',
    },
    {
      label: 'Ready to Deploy',
      value: vehicleAnalytics.summary?.activeVehicles || 0,
      helper: 'Ready for assignment',
      icon: <CheckCircleIcon />,
      main: '#059669',
      soft: '#d1fae5',
    },
    {
      label: 'Deployed Vehicles',
      value: vehicleAnalytics.summary?.assignedVehicles || 0,
      helper: 'Currently in use',
      icon: <AssignmentIcon />,
      main: '#0891b2',
      soft: '#cffafe',
    },
    {
      label: 'Under Maintenance',
      value: typeof underMaintenanceCount === 'number' ? underMaintenanceCount : (vehicleAnalytics.summary?.underMaintenance || 0),
      helper: 'Service and repairs',
      icon: <MaintenanceIcon />,
      main: '#d97706',
      soft: '#fef3c7',
    },
  ]

  return (
    <Box sx={{
      minHeight: '100vh',
      p: { xs: 0.5, sm: 1.5, md: 3 },
      pb: { xs: 3, md: 6 },
      overflowX: 'hidden',
      fontFamily: 'monospace',
      '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiTableCell-root, & .MuiInputBase-root, & .MuiInputLabel-root': {
        fontFamily: 'monospace',
        fontWeight: 950,
        letterSpacing: 0,
      },
    }}>
      {vehicleAnalyticsError && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => refetchVehicleAnalytics()}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          Some older dashboard widgets failed to load. The deployment log above can still be used.
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: { xs: 1.5, md: 2.25 } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h4"
            fontWeight={950}
            sx={{ fontSize: { xs: '1.45rem', md: '2rem' }, lineHeight: 1.15 }}
          >
            Vehicle Management Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
            Fleet readiness, deployment movement, OEM health, and city status in one operating view.
          </Typography>
        </Box>
        <Chip
          icon={<VehicleIcon />}
          label={`${fleetCards[0].value} vehicles`}
          sx={(theme) => ({
            fontWeight: 900,
            color: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
            '& .MuiChip-icon': { color: 'primary.main' },
          })}
        />
      </Stack>

      {/* Fleet Overview Metrics */}
      <Grid container spacing={{ xs: 1, md: 3 }} mb={{ xs: 2, md: 3 }}>
        {fleetCards.map((item) => (
          <Grid item xs={6} sm={6} md={3} key={item.label}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderTop: `3px solid ${item.main}`,
                  pointerEvents: 'none',
                },
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  right: -38,
                  top: -40,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: item.soft,
                  opacity: 0.74,
                },
                '&:hover': {
                  transform: 'translateY(-3px)',
                  borderColor: item.main,
                  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.12)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 1.4, md: 2.5 }, position: 'relative', zIndex: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={900} sx={{ mt: 0.75, color: item.main, lineHeight: 1, fontSize: { xs: '1.45rem', md: '2.125rem' } }}>
                      {item.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {item.helper}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      width: { xs: 34, md: 44 },
                      height: { xs: 34, md: 44 },
                      borderRadius: 2,
                      background: item.soft,
                      color: item.main,
                      '& svg': { fontSize: 25 },
                    }}
                  >
                    {item.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {renderFullDataReport()}

      {renderDeploymentLog()}

      {/* Status by City table */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={getOpsPanelSx}
          >
            <Box sx={getReportTitleBarSx}>
              <Box sx={getReportPillSx}>City Report</Box>
              <Typography
                sx={{
                  fontWeight: 950,
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  fontSize: { xs: '0.86rem', md: '1rem' },
                  lineHeight: 1.2,
                  color: 'inherit',
                }}
              >
                Pan India - Current Vehicle Status - Count (City Wise)
              </Typography>
              <Box sx={getReportPillSx}>
                {selectedStatusCity ? `City: ${selectedStatusCity}` : 'Pan India View'}
              </Box>
            </Box>
            <Box sx={getOpsHeaderSx}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <AssignmentIcon sx={{ color: 'primary.main', fontSize: 23 }} />
                <Box>
                  <Typography sx={{ fontWeight: 950, textTransform: 'uppercase', lineHeight: 1.2 }}>
                    Vehicle Status by City
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    Across {vehicleAnalytics.statusByCity?.cities?.length ?? 0} cities
                  </Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl size="small" sx={getOpsSelectSx}>
                  <InputLabel>Select City</InputLabel>
                  <Select
                    value={selectedStatusCity}
                    label="Select City"
                    onChange={(event) => setSelectedStatusCity(event.target.value)}
                  >
                    <MenuItem value="">All Cities</MenuItem>
                    {(vehicleAnalytics.statusByCity?.cities || []).map((city: string) => (
                      <MenuItem key={city} value={city}>{city}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton onClick={() => refetchVehicleAnalytics()} aria-label="Refresh status by city">
                  <RefreshIcon />
                </IconButton>
              </Stack>
            </Box>
            <CardContent sx={{ p: { xs: 1.25, md: 0 }, overflowX: { xs: 'hidden', md: 'auto' } }}>
              {vehicleAnalytics.statusByCity && vehicleAnalytics.statusByCity.rows && vehicleAnalytics.statusByCity.rows.length > 0 ? (
                <>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Stack spacing={1}>
                      {sortOperationalStatuses(vehicleAnalytics.statusByCity.rows).map((row: any) => (
                        <Box
                          key={row.status}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderLeft: `4px solid ${getOperationalStatusColor(row.status)}`,
                            backgroundColor: 'background.paper',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={800}
                              sx={{ color: getOperationalStatusColor(row.status) }}
                            >
                              {row.status}
                            </Typography>
                            <Chip label={`${row.percentage.toFixed(1)}%`} size="small" color="primary" variant="outlined" />
                          </Stack>
                          <Grid container spacing={1} sx={{ mt: 0.5 }}>
                            {vehicleAnalytics.statusByCity.cities.map((city: string, i: number) => {
                              const count = row.counts[i] || 0;
                              const cityTotal = vehicleAnalytics.statusByCity.totalPerCity[i] || 0;
                              return (
                                <Grid
                                  item
                                  xs={6}
                                  key={city}
                                  sx={(theme) => ({
                                    p: 0.8,
                                    borderRadius: 1,
                                    ...getOperationalStatusCellSx(theme, row.status),
                                    ...(selectedStatusCity === city ? getSelectedHighlightSx(theme) : {}),
                                  })}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                                    {city}
                                  </Typography>
                                  <Typography variant="body2" fontWeight={700}>{count.toLocaleString()}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
                                    ({formatContributionPercent(count, cityTotal)})
                                  </Typography>
                                </Grid>
                              );
                            })}
                          </Grid>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            Total: {row.total}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                  <Table size="small" sx={{ display: { xs: 'none', md: 'table' }, ...getOpsTableSx() }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Overall Status</TableCell>
                        {vehicleAnalytics.statusByCity.cities.map((city: string) => (
                          <TableCell
                            key={city}
                            align="right"
                            sx={(theme) => ({
                              fontWeight: 'bold',
                              ...(selectedStatusCity === city ? getSelectedHighlightSx(theme) : {}),
                            })}
                          >
                            {city}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Percentage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortOperationalStatuses(vehicleAnalytics.statusByCity.rows).map((row: any) => (
                        <TableRow key={row.status} hover>
                          <TableCell
                            sx={{
                              borderLeft: `4px solid ${getOperationalStatusColor(row.status)}`,
                              color: getOperationalStatusColor(row.status),
                              fontWeight: 800,
                            }}
                          >
                            {row.status}
                          </TableCell>
                          {row.counts.map((c: number, i: number) => (
                            <TableCell
                              key={i}
                              align="right"
                              sx={(theme) => ({
                                ...getOperationalStatusCellSx(theme, row.status),
                                ...(selectedStatusCity === vehicleAnalytics.statusByCity.cities[i] ? getSelectedHighlightSx(theme) : {}),
                              })}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 850, lineHeight: 1.05 }}>
                                {c.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.15 }}>
                                ({formatContributionPercent(c, vehicleAnalytics.statusByCity.totalPerCity[i] || 0)})
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.total}</TableCell>
                          <TableCell align="right">{row.percentage.toFixed(2)}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow sx={{ backgroundColor: '' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                        {vehicleAnalytics.statusByCity.totalPerCity.map((t: number, i: number) => (
                          <TableCell
                            key={i}
                            align="right"
                            sx={(theme) => ({
                              fontWeight: 'bold',
                              ...(selectedStatusCity === vehicleAnalytics.statusByCity.cities[i] ? getSelectedHighlightSx(theme) : {}),
                            })}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
                              {t.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.15 }}>
                              ({formatContributionPercent(t, vehicleAnalytics.statusByCity.grandTotal)})
                            </Typography>
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{vehicleAnalytics.statusByCity.grandTotal}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>100%</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">No city-wise status data available.</Typography>
              )}
            </CardContent>
          </Paper>
        </Grid>
      </Grid>

      {/*Vehicle Status and OEM Analytics Row */}
        {/* <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <VehicleChart
              title="Vehicle Status Distribution"
              subtitle="Current operational status"
              data={(vehicleAnalytics.statusDistribution || []).map((status: any) => ({
                label: status.status,
                value: status.count,
                color: status.status === 'Available' ? '#4caf50' : 
                      status.status === 'Assigned' ? '#2196f3' : 
                      status.status === 'Under Maintenance' ? '#ff9800' : '#f44336',
              }))}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <VehicleChart
              title="OEM Distribution"
              subtitle="Vehicles by manufacturer"
              data={(vehicleAnalytics.oemAnalytics || []).slice(0, 5).map((oem: any, index: number) => ({
                label: oem.oem,
                value: oem.vehicleCount,
                color: ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0'][index % 5],
              }))}
            />
          </Grid>
        </Grid> */}


      {/* OEM operational status analytics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          {renderOemStatusAnalyticsCard()}
        </Grid>
      </Grid>


      {renderOemModelStatusTable()}

      {renderOemFleetDistributionTable()}



      {/* Fleet Performance Metrics */}
      {/* <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <CardHeader 
              title="Fleet Performance Metrics" 
              avatar={<SpeedIcon color="primary" />}
              action={
                <IconButton>
                  <MoreVertIcon />
                </IconButton>
              }
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {fleetPerformance.fleet?.utilizationRate || 0}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Utilization Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {fleetPerformance.fleet?.averageAge || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Age (months)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold" color="info.main">
                      {(fleetPerformance.fleet?.averageMileage || 0).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Mileage (km)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                      {formatCurrency(fleetPerformance.costs?.costPerVehicle || 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Cost per Vehicle
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <CardHeader 
              title="Cost Analytics" 
              avatar={<CostIcon color="primary" />}
            />
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="body2" color="textSecondary">Total Maintenance</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(fleetPerformance.costs?.totalMaintenanceCosts || 0)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="body2" color="textSecondary">Cost per KM</Typography>
                <Typography variant="body2" fontWeight="medium">
                  ₹{(fleetPerformance.costs?.costPerKm || 0).toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="body2" color="textSecondary">Service Cost</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(serviceAnalytics.summary?.totalCost || 0)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">Damage Cost</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatCurrency(damageAnalytics.summary?.totalCost || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Service and Damage Analytics */}
      {/* <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <VehicleChart
            title="Service Type Distribution"
            subtitle={`Services in ${timeRange}`}
            data={Object.entries(serviceAnalytics.serviceTypeDistribution || {}).map(([type, count]: [string, any], index) => ({
              label: type,
              value: count,
              color: ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0', '#607d8b'][index % 6],
            }))}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <VehicleChart
            title="Damage Severity Analysis"
            subtitle={`Damages in ${timeRange}`}
            data={Object.entries(damageAnalytics.severityDistribution || {}).map(([severity, count]: [string, any]) => ({
              label: severity,
              value: count,
              color: severity === 'Low' ? '#4caf50' : 
                     severity === 'Medium' ? '#ff9800' : 
                     severity === 'High' ? '#f44336' : '#9c27b0',
            }))}
          />
        </Grid>
      </Grid> */}

      {/* Bottom Row: Recent Activities and Top Vehicles */}
      {/* <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <CardHeader 
              title="OEM Performance Overview" 
              avatar={<OEMIcon color="primary" />}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>OEM</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(vehicleAnalytics.oemAnalytics || []).slice(0, 5).map((oem: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{oem.oem}</TableCell>
                        <TableCell>{oem.model}</TableCell>
                        <TableCell align="right">{oem.vehicleCount}</TableCell>
                        <TableCell>
                          <Chip 
                            label={oem.vehicleType} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid> */}

        {/* <Grid item xs={12} md={6}>
          <Card
            elevation={0}
            sx={{
              height: '100%',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
            }}
          >
            <CardHeader 
              title="Fleet Health Indicators" 
              avatar={<AnalyticsIcon color="primary" />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      {fleetPerformance.efficiency?.serviceFrequency || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Services This Period
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <Typography variant="h6" color="warning.main" fontWeight="bold">
                      {fleetPerformance.efficiency?.damageFrequency || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Damages This Period
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <Typography variant="h6" color="info.main" fontWeight="bold">
                      {serviceAnalytics.summary?.averageCost ? formatCurrency(serviceAnalytics.summary.averageCost) : '₹0'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Service Cost
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2}>
                    <Typography variant="h6" color="error.main" fontWeight="bold">
                      {damageAnalytics.summary?.averageCost ? formatCurrency(damageAnalytics.summary.averageCost) : '₹0'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Damage Cost
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid> */}
      {/* </Grid> */}
    </Box>
  )
}

export default VehicleDashboard
